import React from "react";
import {
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import FlagEmoji from "../../../../components/common/FlagEmoji";
import {
  Description as DescriptionIcon,
  InsertDriveFile as InsertDriveFileIcon,
  CloudUpload as CloudUploadIcon,
  Clear as ClearIcon,
  FileCopy as FileCopyIcon,
} from '@mui/icons-material';
import { Upload } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { useSearchTokens } from "../../../SearchScreen/searchDesignTokens";

const ImportOption = ({
  importedFile,
  contractName,
  setContractName,
  description,
  setDescription,
  error,
  supportedFormats,
  handleDragEnter,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  creating,
  inputRef,
  getAcceptedFileTypes,
  handleFileImport,
  handleRemoveFile,
  isDragging,
  selectedCountry,
  setSelectedCountry,
  onCountryChange,
}) => {
  const { t } = useTranslation();
  const dk = useSearchTokens();

  const countryOptions = [
    { value: 'MA',  flag: '🇲🇦', name: 'Maroc' },
    { value: 'AE',  flag: '🇦🇪', name: 'UAE' },
  ];

  const triggerFileInput = () => { if (inputRef.current) inputRef.current.click(); };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem',
      backgroundColor: dk.inputBg,
      borderRadius: '8px',
      '& fieldset': { borderColor: dk.border },
      '&:hover fieldset': { borderColor: dk.borderHover },
      '&.Mui-focused fieldset': { borderColor: dk.goldBdr, borderWidth: 1 },
    },
    '& .MuiInputLabel-root': {
      color: dk.text3,
      fontSize: '0.8rem',
      '&.Mui-focused': { color: dk.gold },
    },
    '& .MuiSelect-select': { color: dk.text1 },
    '& .MuiSvgIcon-root': { color: dk.text3 },
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem',
      backgroundColor: dk.inputBg,
      borderRadius: '8px',
      color: dk.text1,
      '& fieldset': { borderColor: dk.border },
      '&:hover fieldset': { borderColor: dk.borderHover },
      '&.Mui-focused fieldset': { borderColor: dk.goldBdr, borderWidth: 1 },
    },
    '& .MuiInputLabel-root': {
      color: dk.text3,
      fontSize: '0.8rem',
      '&.Mui-focused': { color: dk.gold },
    },
    '& .MuiInputBase-input': { color: dk.text1 },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: dk.text3, fontSize: '1.1rem' },
    '& .MuiFormHelperText-root': { fontSize: '0.7rem' },
  };

  const sectionLabel = {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: dk.text3,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    mb: 1,
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Header */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: '1.1rem',
            fontWeight: 500,
            color: dk.text1,
            mb: 0.5,
          }}
        >
          {t("import_option_title") || "Import Document"}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: dk.text3, lineHeight: 1.6 }}>
          {t("import_option_subtitle") || "Upload an existing document to start your contract"}
        </Typography>
      </Box>

      {/* Country */}
      <Box>
        <Typography sx={sectionLabel}>{t("select_country") || "Jurisdiction"}</Typography>
        <FormControl fullWidth size="small" sx={selectSx}>
          <InputLabel>{t("country") || "Country"}</InputLabel>
          <Select
            value={selectedCountry || ""}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              if (onCountryChange) onCountryChange(e.target.value);
            }}
            label={t("country") || "Country"}
            renderValue={(value) => {
              if (!value) return <em style={{ color: dk.text3 }}>{t("select_country_first") || "Select a country"}</em>;
              const c = countryOptions.find(o => o.value === value);
              return c ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagEmoji emoji={c.flag} size={18} />
                  <span>{c.name}</span>
                </Box>
              ) : value;
            }}
          >
            <MenuItem value=""><em>{t("select_country_first") || "Select a country"}</em></MenuItem>
            {countryOptions.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagEmoji emoji={c.flag} size={18} />
                  <span>{c.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Drop zone */}
      <Box
        sx={{
          border: `1px dashed ${isDragging ? dk.gold : dk.borderHover}`,
          borderRadius: '10px',
          p: 3,
          textAlign: 'center',
          backgroundColor: isDragging ? dk.goldSub : dk.elevated,
          transition: 'all 0.2s ease',
          cursor: creating ? 'not-allowed' : 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 160,
          '&:hover': !creating ? {
            borderColor: dk.gold,
            backgroundColor: dk.goldSub,
          } : {},
        }}
        onClick={!creating ? triggerFileInput : undefined}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={inputRef}
          onChange={handleFileImport}
          accept={getAcceptedFileTypes()}
          style={{ display: 'none' }}
          disabled={creating}
        />

        {importedFile ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: '8px',
              backgroundColor: dk.goldSub,
              border: `1px solid ${dk.goldBdr}`,
              width: '100%',
            }}
          >
            <FileCopyIcon sx={{ color: dk.gold, fontSize: 22, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: dk.text1 }} noWrap>
                {importedFile.name}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: dk.text3 }}>
                {(importedFile.size / 1024).toFixed(0)} KB
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }}
              disabled={creating}
              sx={{
                color: dk.text3,
                '&:hover': { color: dk.error, backgroundColor: 'rgba(248,113,113,0.08)' },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: dk.goldSub,
                border: `1px solid ${dk.goldBdr}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
                opacity: creating ? 0.5 : 1,
              }}
            >
              <Upload size={18} color={isDragging ? dk.gold : dk.text3} />
            </Box>
            <Typography
              sx={{
                fontSize: '0.84rem',
                fontWeight: 500,
                color: isDragging ? dk.gold : dk.text2,
                mb: 0.5,
              }}
            >
              {isDragging
                ? (t("drop_file_here") || "Drop your file here")
                : (t("drag_drop_or_click") || "Drag & drop or click to browse")}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: dk.text3 }}>
              {supportedFormats.map(f => f.label).join(' · ')}
            </Typography>
          </>
        )}
      </Box>

      {/* Contract details */}
      <Box>
        <Typography sx={sectionLabel}>{t("contract_details") || "Contract Details"}</Typography>
        <TextField
          fullWidth
          size="small"
          label={t("contract_title") || "Contract Title"}
          value={contractName}
          onChange={(e) => setContractName(e.target.value)}
          error={!!error && !contractName.trim()}
          helperText={!contractName.trim() && error ? t("name_required") || "Contract name required" : ""}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <InsertDriveFileIcon />
              </InputAdornment>
            ),
          }}
          sx={{ ...textFieldSx, mb: 1.5 }}
        />
        <TextField
          fullWidth
          size="small"
          label={t("description_optional") || "Description (Optional)"}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DescriptionIcon />
              </InputAdornment>
            ),
          }}
          sx={textFieldSx}
        />
      </Box>
    </Box>
  );
};

export default ImportOption;
